import React, {
  FC,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import { Group } from '@visx/group';
import { Pie, Arc } from '@visx/shape';
import { scaleLinear } from '@visx/scale';
import { useTooltip } from '@visx/tooltip';
import ChartTooltip from '../../components/ChartTooltip';
import { localPoint } from '@visx/event';
import PieChartImage from '../../components/ChartsImages/PieChartImage';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import {
  FormatUtils,
  getColors,
  METRICS,
  useWindowSize,
  getPopulationPerc,
  isReach
} from '../../utils';
import styled from 'styled-components';
import uniqid from 'uniqid';
import { BoxV2, EmptyStateV2 } from '@cubeyou-team/hx-react-components';

const StyledPieChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  padding: 10px;
`;

type PieChartProps = {
  margin: any;
  data: any[];
  hasInnerRadius: boolean;
  metric: string;
  color: string;
  emptyDataMessage: string;
  limit: any;
  showItemsPreview?: boolean;
  limitText?: number;
  showMarkers?: boolean;
  /** Radians; markers skip narrower arcs. `0` shows for every slice. */
  minMarkerArcAngle?: number;
};

let tooltipTimeout: number;
const defaultMargin = { top: 50, right: 50, bottom: 50, left: 100 };

const PieChart: FC<PieChartProps> = ({
  data,
  margin = defaultMargin,
  hasInnerRadius,
  metric,
  color,
  emptyDataMessage,
  limit = 'auto',
  showItemsPreview,
  limitText = 25,
  showMarkers = true,
  minMarkerArcAngle
}) => {
  const minArc =
    minMarkerArcAngle !== undefined && minMarkerArcAngle !== null
      ? minMarkerArcAngle
      : 0.01;
  const svgRef = useRef(null);
  const chartId = useRef(uniqid());
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();

  const windowSize = useWindowSize();

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  const radius = Math.min(innerWidth, innerHeight) / 2;
  const centerY = innerHeight / 2;
  const centerX = innerWidth / 2;
  const isEmpty = !data || data.length === 0;

  const filteredData = useMemo(() => {
    if (isEmpty) {
      return [];
    }
    const filtered = limit !== 'auto' ? data.slice(0, limit) : data;
    return filtered.filter((d) => d[metric] && d[metric] > 0);
  }, [isEmpty, data, limit, metric]);

  const colorScale = useMemo(
    () =>
      scaleLinear<string>({
        domain: [
          0,
          isEmpty ? 0 : filteredData.length / 2,
          isEmpty ? 0 : filteredData.length
        ],
        range: getColors(color)
      }),
    [isEmpty, filteredData, color]
  );

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, index) => {
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      const eventSvgCoords = localPoint(svgRef.current, event);
      const color = filteredData[index].color || colorScale(index);
      showTooltip({
        tooltipData: {
          ...filteredData[index],
          color
        },
        tooltipTop: eventSvgCoords?.y,
        tooltipLeft: eventSvgCoords?.x
      });
    },
    [showTooltip, filteredData, colorScale]
  );

  if (isEmpty) {
    return (
      <StyledPieChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledPieChartWrapper>
    );
  }

  return (
    <StyledPieChartWrapper ref={chartRef}>
      <svg ref={svgRef} width={width} height={height}>
        <rect width={width} height={height} fill='#ffffff' rx={14} />
        <Group
          top={centerY + margin.top}
          left={centerX + margin.left}
          key={`group-${chartId.current}`}
        >
          <Pie
            data={filteredData}
            pieValue={(d) =>
              metric === METRICS.BASE ? d[metric] * 100 : d[metric]
            }
            pieSortValues={() => -1}
            outerRadius={radius - 2 * 1.3}
          >
            {(pie) => {
              return pie.arcs.map((arc, index) => {
                const [centroidX, centroidY] = pie.path.centroid(arc);
                const point = (arc.startAngle + arc.endAngle) / 2 - Math.PI / 2;

                const offsetX =
                  Math.cos(point) * (radius / 2) < 0
                    ? Math.cos(point) * (radius / 2) - 30
                    : Math.cos(point) * (radius / 2) + 30;
                const offsetY =
                  Math.sin(point) * (radius / 2) < 0
                    ? Math.sin(point) * (radius / 2) - 30
                    : Math.sin(point) * (radius / 2) + 30;
                return (
                  <Group key={`arc-${chartId.current}-${index}`}>
                    <Arc
                      startAngle={arc.startAngle}
                      endAngle={arc.endAngle}
                      innerRadius={hasInnerRadius ? radius / 2 : 0}
                      cornerRadius={1}
                      outerRadius={radius}
                      padAngle={arc.padAngle}
                      padRadius={2}
                      fill={filteredData[index].color || colorScale(index)}
                      stroke='#fff'
                      strokeWidth='1'
                      onMouseMove={(event) => handleMouseMove(event, index)}
                      onMouseLeave={(event) => handleMouseLeave(event)}
                    />
                    {showMarkers &&
                      (minArc === 0 ||
                        arc.endAngle - arc.startAngle > minArc) && (
                        <PieChartImage
                          d={{
                            ...arc.data,
                            x: centroidX + offsetX,
                            y: centroidY + offsetY,
                            index
                          }}
                          color={filteredData[index].color || colorScale(index)}
                          showItemsPreview={showItemsPreview}
                          chartId={chartId.current}
                          limitText={limitText}
                          onMouseMove={handleMouseMove}
                          onMouseLeave={handleMouseLeave}
                        />
                      )}
                  </Group>
                );
              });
            }}
          </Pie>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          {!Object.values(METRICS).includes(metric) && !isReach(metric) ? (
            <div>
              <span>Value: </span>
              {tooltipData[metric]}
            </div>
          ) : (
            <Fragment>
              <div>
                <span>Affinity: </span>
                {FormatUtils.formattedValue(
                  tooltipData.target_audience_affinity,
                  METRICS.AFFINITY
                )}
              </div>
              <div>
                <span>Reach: </span>
                {FormatUtils.formattedValue(
                  getPopulationPerc(tooltipData),
                  METRICS.REACH
                )}
              </div>
              <div>
                <span>Base: </span>
                {FormatUtils.formattedValue(
                  tooltipData.base_population_perc,
                  METRICS.BASE
                )}
              </div>
              <div>
                <span>Penetration: </span>
                {FormatUtils.formattedValue(
                  tooltipData.penetration,
                  METRICS.PENETRATION
                )}
              </div>
            </Fragment>
          )}
        </ChartTooltip>
      )}
    </StyledPieChartWrapper>
  );
};

export default PieChart;
